import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Zap, Eye, Plus } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1572521165329-b197f9ea3da6?crop=entropy&cs=srgb&fm=jpg&w=400&q=70&ixlib=rb-4.1.0";

export default function MyListings() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/listings/mine").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-safety">Your inventory</div>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">My listings</h1>
        </div>
        <Button
          asChild
          data-testid="mylistings-new"
          className="rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6"
        >
          <Link to="/sell"><Plus className="w-4 h-4 mr-2" /> New listing</Link>
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-10 border border-border bg-white p-16 text-center">
          <div className="font-display text-2xl tracking-tight text-forest">Nothing here yet</div>
          <p className="mt-2 text-sm text-muted-foreground">Post your first listing in under 2 minutes.</p>
          <Button asChild className="mt-6 rounded-none bg-safety hover:bg-safety-hover text-white h-11 px-6" data-testid="mylistings-empty-cta">
            <Link to="/sell">Post a listing →</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-8 border border-border bg-white">
          {items.map((l, i) => (
            <div
              key={l.listing_id}
              data-testid={`mylistings-row-${l.listing_id}`}
              className={`grid grid-cols-[80px,1fr,auto] items-center gap-4 p-4 ${i > 0 ? "border-t border-border" : ""}`}
            >
              <img
                src={l.images?.[0] ? fileUrl(l.images[0]) : PLACEHOLDER}
                onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                alt=""
                className="w-20 h-20 object-cover border border-border"
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {l.status === "pending" && <Badge className="rounded-none bg-safety text-white text-[10px] overline">Pending review</Badge>}
                  {l.status === "flagged" && <Badge className="rounded-none bg-destructive text-white text-[10px] overline">Flagged</Badge>}
                  {l.is_featured && <Badge className="rounded-none bg-forest text-white text-[10px] overline">Featured</Badge>}
                  {l.is_clearance && <Badge className="rounded-none bg-safety text-white text-[10px] overline">Clearance</Badge>}
                  <div className="overline text-muted-foreground text-[10px]">{l.category}</div>
                </div>
                <div className="mt-1 font-display text-lg tracking-tight truncate">{l.title}</div>
                <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="tabular-nums">£{Number(l.price).toFixed(2)}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{l.views || 0}</span>
                  <span>{l.location}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm" className="rounded-none">
                  <Link to={`/listings/${l.listing_id}`} data-testid={`mylistings-view-${l.listing_id}`}>View</Link>
                </Button>
                  <Button asChild variant="outline" size="sm" className="rounded-none">
                    <Link to={`/sell/${l.listing_id}`} data-testid={`mylistings-edit-${l.listing_id}`}>Edit</Link>
                  </Button>
                {!l.is_featured && (
                  <Button
                    onClick={() => nav(`/upgrade/${l.listing_id}`)}
                    size="sm"
                    data-testid={`mylistings-feature-${l.listing_id}`}
                    className="rounded-none bg-safety hover:bg-safety-hover text-white"
                  >
                    <Zap className="w-4 h-4 mr-1" /> Feature
                  </Button>
                )}
                <Button
                  onClick={() => del(l.listing_id)}
                  variant="outline"
                  size="sm"
                  data-testid={`mylistings-delete-${l.listing_id}`}
                  className="rounded-none border-safety text-safety hover:bg-safety hover:text-white"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
